import Model from '@/app/Model'

import { Department } from '@/models/Department'
import { Role } from '@/models/Role'
import { Company } from '@/models/Company'
import { Permission } from '@/models/Permission'


const DEPARTMENT_RESTRICTED_PERMISSIONS = [
	'VIEW_OEM_SUPPLIER',
	'VIEW_INVOICE'
]

// Mirrors recar-backend recar/core/validation.py: delay_payment,
// delay_payment_days and limit may only be edited by these roles,
// and only in GTV departments.
const CLIENT_FINANCIAL_ROLES = ['Įmonės vadovas', 'Kasininkas', 'Buhaltere']
const GTV_CLIENT_DEPARTMENT_IDS = [540, 623, 9186]
export class User extends Model {
	constructor (payload = {}) {
		super(payload)
		// Non-enumerable memo store so Vue's reactivity does not deep-observe it
		Object.defineProperty(this, '_permissionsMemo', {
			value: {
				signature: null,
				permissions: null,
				departmentMaps: new Map()
			},
			writable: true,
			enumerable: false,
			configurable: true
		})
	}

	static fields () {
		return {
			admin: false,
			id: null,
			email: null,
			firstname: null,
			lastname: null,
			phoneNumber: null,
			picture: null,
			_selectedDepartment: null,
			primaryDepartmentId: null,
			selectedDepartmentId: null,
			selectedVehicleType: null,
			selectedCartId: null,
			locationGroups: null,
			accountingLocation: null,
			userSales: null,
			userSalesWithVat: null,
			selectedCompanyId: null,
			shippingRulesAccepted: null,
			verified: false,
			mfaRequired: false,
			mfaGraceExpiresAt: null
		}
	}

	static hasMany () {
		return {
			departments: Department,
			companies: Company,
			roles: Role
		}
	}

	get plan () {
		return this.selectedDepartment?.plan || null
	}

	get currency () {
		return this.selectedDepartment && this.selectedDepartment.currency || null
	}

	get selectedDepartment () {
		return this._selectedDepartment  && this._selectedDepartment.id === this.selectedDepartmentId ? this._selectedDepartment  : this.departments.find(
			department => department.id === this.selectedDepartmentId
		)
	}

	get isMoto () {
		if (this.selectedVehicleType) {
			return this.selectedVehicleType === 'moto'
		}
		return false
	}

	get isAuto () {
		if (this.selectedVehicleType) {
			return this.selectedVehicleType === 'auto'
		}
		return false
	}

	get isCommercialVehicle () {
		if (this.selectedVehicleType) {
			return this.selectedVehicleType === 'commercial_vehicle'
		}
		return false
	}

	get partnerships () {
		return this.roles.filter(role => role.name.includes('partnership'))
	}

	get hasPartnership () {
		return this.roles.some(role => role.name.includes('partnership'))
	}

	get _permissionsSignature () {
		// const departmentValues = this.departments.map(department => [
		// 	department.id,
		// 	department.tasksFlowEnabled,
		// 	department.partsQuantityEnabled,
		// 	department.oemPartsEnabled,
		// 	department.groupedSaleLimitingEnabled,
		// 	department.returningShipmentsEnabled,
		// 	department.numberPlatesSearchEnabled,
		// 	department.billingManagementEnabled
		// ].join(':'))

		// const roleValues = this.roles.map(role => [
		// 	role.id,
		// 	role.departmentId,
		// 	role.companyId,
		// 	role.departmentPermissions.map(permission => permission.name).join(','),
		// 	role.companyPermissions.map(permission => permission.name).join(','),
		// 	role.distributorPermissions.map(permission => permission.name).join(',')
		// ].join(':'))

		//Commented for performance reasons, page becomes too slow

		const selectedDepartment = this.selectedDepartment
		return [
			this.admin,
			this.selectedDepartmentId,
			this.selectedCompanyId,
			this.selectedVehicleType,
			this.roles,
			this.departments,
			// ...departmentValues,
			// ...roleValues,
			selectedDepartment ? selectedDepartment.oemPartsEnabled : null,
			selectedDepartment ? selectedDepartment.groupedSaleLimitingEnabled : null,
			selectedDepartment ? selectedDepartment.returningShipmentsEnabled : null,
			selectedDepartment ? selectedDepartment.numberPlatesSearchEnabled : null,
			selectedDepartment ? selectedDepartment.billingManagementEnabled : null,
			selectedDepartment ? selectedDepartment.partsQuantityEnabled : null,
			selectedDepartment ? selectedDepartment.tasksFlowEnabled : null
		]
	}

	_isPermissionsMemoValid (memo) {
		const signature = this._permissionsSignature
		return (
			memo.signature !== null &&
			memo.signature.length === signature.length &&
			memo.signature.every((value, index) => value === signature[index])
		)
	}

	get permissions () {
		const memo = this._permissionsMemo
		if (memo.permissions && this._isPermissionsMemoValid(memo)) {
			return memo.permissions
		}

		const permissions = []
		const seen = new Set()
		const pushUnique = (permission) => {
			if (!seen.has(permission)) {
				seen.add(permission)
				permissions.push(permission)
			}
		}
		const pushAll = (list) => {
			list.forEach(pushUnique)
		}
		const selectedDepartment = this.selectedDepartment

		this.roles.forEach(role => {
			if (
				this.admin !== null &&
				role.distributorPermissions.length
				// !parseInt(this.selectedCompanyId) &&
				// !parseInt(this.selectedDepartmentId)
			) {
				pushAll(role.distributorPermissions)
			}

			if (this.selectedDepartmentId) {
				pushUnique(new Permission({ id: 9995, name: 'HAS_SELECTED_DEPARTMENT' }))
			}

			if (
				role.companyId !== null &&
				parseInt(role.companyId) === parseInt(this.selectedCompanyId)
			) {
				// pushAll(role.companyPermissions)
				pushAll(role.departmentPermissions)
			}
			if (
				role.departmentId !== null &&
				parseInt(role.departmentId) === parseInt(this.selectedDepartmentId)
			) {
				pushAll(role.departmentPermissions)
			}
		})
		if (
			this.selectedVehicleType !== 'oem'
		) {
			pushUnique(new Permission({ id: 9996, name: 'SHOW_GROUPED_SALES' }))
		}
		if (
			(selectedDepartment?.oemPartsEnabled)  ||
			(!selectedDepartment && this.admin)
		) {
			pushUnique(new Permission({ id: 9993, name: 'OEM_ENABLED' }))
		}
		if (
			(selectedDepartment?.groupedSaleLimitingEnabled)
			 || (!selectedDepartment && this.admin)
		) {
			pushUnique(new Permission({ id: 9992, name: 'GROUPED_SALE_LIMITING_ENABLED' }))
		}
		if (
			(selectedDepartment?.returningShipmentsEnabled)
			 || (!selectedDepartment && this.admin)
		) {
			pushUnique(new Permission({ id: 9991, name: 'CREATE_RETURNING_SHIPMENT' }))
		}
		if (
			(selectedDepartment?.numberPlatesSearchEnabled)
		) {

			pushUnique(new Permission({ id: 9990, name: 'VIEW_LICENCE_PLATE' }))
		}
		if (
			(selectedDepartment?.billingManagementEnabled)
		) {

			pushUnique(new Permission({ id: 9989, name: 'VIEW_BILLING_MANAGEMENT' }))
		}
		if (
			(selectedDepartment?.partsQuantityEnabled === false)  ||
			(!selectedDepartment && this.admin)
		) {
			pushUnique(new Permission({ id: 9997, name: 'PARTS_QUANTITY_DISABLED' }))
		}
		if (
			(selectedDepartment?.tasksFlowEnabled) ||
			(!selectedDepartment && this.admin)
		) {
			pushUnique(new Permission({ id: 9998, name: 'TASK_FLOW_ENABLED' }))
		}
		if (this.admin) {
			pushUnique(new Permission({ id: 9999, name: 'IS_ADMIN' }))
		}

		if (permissions.find(permission => permission.name === 'VIEW_PART') && !permissions.find(_permission => _permission.name === 'HIDE_RESTRICTED_GROUPED_SALE') ) {
			pushUnique(new Permission({ id: 9994, name: 'SHOW_PARTS' }))
		}

		memo.signature = this._permissionsSignature
		memo.permissions = permissions
		memo.departmentMaps.clear()

		return permissions
	}

	permissionForCompany (id) {
		const permissions = new Map()
		this.roles.forEach(role => {
			if (
				role.companyId === null ||
				parseInt(role.companyId) === parseInt(id)
			) {
				role.departmentPermissions.forEach(permission => {
					permissions.set(permission.name, permission)
				})
				role.companyPermissions.forEach(permission => {
					permissions.set(permission.name, permission)
				})
			}
		})
		return Array.from(permissions.values())
	}

	companiesWithPermission (permissionName) {
		const companyIds = new Set()
		for (const role of this.roles) {
			const hasPermission =
				role.departmentPermissions.some(permission => permission.name === permissionName) ||
				role.companyPermissions.some(permission => permission.name === permissionName)
			if (!hasPermission) {
				continue
			}
			if (role.companyId === null) {
				return null
			}
			companyIds.add(String(role.companyId))
		}
		return companyIds
	}

	permissionForDepartment (toCheck, departmentId) {
		const permissions = this.departmentPermissions(departmentId)
		return permissions.has(toCheck)
	}

	departmentPermissions (departmentId) {
		const memo = this._permissionsMemo
		if (!this._isPermissionsMemoValid(memo)) {
			memo.signature = this._permissionsSignature
			memo.permissions = null
			memo.departmentMaps.clear()
		}
		if (memo.departmentMaps.has(departmentId)) {
			return memo.departmentMaps.get(departmentId)
		}

		const roles = this.roles.filter(role => role.departmentId === departmentId)
		const department = this.departments.find(dep => dep.id === departmentId)
		const permissions = new Map()
		roles.forEach(role => {
			role.departmentPermissions.forEach(permission => {
				permissions.set(permission.name, permission)
			})
		})

		if (permissions.get('VIEW_GROUPED_SALE')) {
			permissions.set('SHOW_GROUPED_SALES', new Permission({ id: 9996, name: 'SHOW_GROUPED_SALES' }))
		}

		if (permissions.get('VIEW_PART')) {
			permissions.set('SHOW_PARTS', new Permission({ id: 9994, name: 'SHOW_PARTS' }))
		}

		if (department?.tasksFlowEnabled) {
			permissions.set('TASK_FLOW_ENABLED', new Permission({ id: 9998, name: 'TASK_FLOW_ENABLED' }))
		}
		if (!department?.partsQuantityEnabled)  {
			permissions.set('PARTS_QUANTITY_DISABLED', new Permission({ id: 9997, name: 'PARTS_QUANTITY_DISABLED' }))
		}

		if (department?.oemPartsEnabled) {
			permissions.set('OEM_ENABLED', new Permission({ id: 9993, name: 'OEM_ENABLED' }))
		}

		if (!this.selectedDepartmentId) {
			// Hide restricted permissions when no department is selected.
			// When a department is selected, the role's department permissions
			// intentionally remain available (e.g. Suppliers/Invoice access).
			// Any change to this behavior must be covered by an authorization test.
			DEPARTMENT_RESTRICTED_PERMISSIONS.forEach(permission => {
				permissions.delete(permission)
			})
		}


		memo.departmentMaps.set(departmentId, permissions)
		return permissions
	}

	get managerRole () {
		if (this.selectedDepartmentRole && this.selectedDepartmentRole.name === 'Padalinio vadovas') {
			return this.selectedDepartmentRole
		}
		if (this.selectedCompanyRole && this.selectedCompanyRole.name === 'Įmonės vadovas') {
			if (this.selectedDepartmentRole) {
				return {
					...this.selectedCompanyRole,
					departmentId: this.selectedDepartmentRole.departmentId
				}
			}
			return this.selectedCompanyRole
		}
		return null
	}

	canEditClientFinancialFields (departmentId) {
		const depId = parseInt(departmentId)
		if (!GTV_CLIENT_DEPARTMENT_IDS.includes(depId)) {
			return true
		}
		return this._hasClientFinancialRole(depId)
	}

	canEditClientLockedField (departmentId) {
		return this._hasClientFinancialRole(parseInt(departmentId))
	}

	_hasClientFinancialRole (depId) {
		if (this.admin) {
			return true
		}
		const toId = value => value !== null && value !== undefined ? parseInt(value) : null
		const department = this.departments.find(dep => toId(dep.id) === depId)
		const companyId = department ? toId(department.companyId) : null
		return this.roles.some(role => {
			if (!CLIENT_FINANCIAL_ROLES.includes(role.name)) {
				return false
			}
			if (toId(role.departmentId) === depId) {
				return true
			}
			return department !== undefined && toId(role.companyId) === companyId
		})
	}

	hasPermission (toCheck, depId=null) {
		if (depId && depId !== this.selectedDepartmentId) {
			return this.permissionForDepartment(toCheck, depId)
		}
		return this.permissions.find(permission => permission.name === toCheck)
	}

	hasSomePermission (toCheck) {
		if (this.admin || this.selectedDepartmentId) {
			return this.hasPermission(toCheck)
		}
		return this.hasSomeDepartmentPermission(toCheck)
	}


	hasSomeDepartmentPermission (toCheck) {
		return this.roles.some(role => this.permissionForDepartment(toCheck, role.departmentId))
	}

	hasEveryDepartmentPermission (toCheck) {
		return this.roles.every(role => this.permissionForDepartment(toCheck, role.departmentId))
	}

	selectDepartment (id) {
		this.selectedDepartmentId = id
	}
	get selectedDepartmentRole () {
		if (this.selectedDepartmentId) {
			return this.roles.find(role => parseInt(role.departmentId) === parseInt(this.selectedDepartmentId))
		}
		return null
	}
	get selectedCompanyRole () {
		if (this.selectedCompanyId) {
			return this.roles.find(role => parseInt(role.companyId) === parseInt(this.selectedCompanyId))
		}
		return null
	}

}

export function createUser (data) {
	// return Object.freeze(new User(data))
	const { selectedDepartment, ...restData } = data
	return new User({
		...restData,
		_selectedDepartment: selectedDepartment
	})
}
